import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import { useHistory } from 'react-router-dom';

// mui components
import {
    Typography,
    FormControl,
    Pagination,
    IconButton,
    Tooltip,
    Card,
    Button,
    Select,
    MenuItem,
    Stack,
    Collapse,
    Grid,
    TextField
} from "@mui/material";
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; 
import InventoryIcon from '@mui/icons-material/Inventory';
// Chart Core Imports
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend } from 'recharts';
// Shared standard drawers & sub-components
import LineItemsLogFilters from './Filters'; 
import NorowsData from '../../../pages/General/NorowsData'; 
import TrackingDrawer from '../../General/TrackingDrawer';
import OrderDrawer from '../../General/OrderDrawer';
import CarrierTracking from '../../General/CarrierTracking'; 
import PageSearch from '../../General/PageSearch'

// Redux actions
import { fetchOrderDetail } from '../../OrderDetail/orderdetailsSlice';
import { fetchCustomerDetails } from '../Deliveries/trackingEventsSlice';
import { fetchLineItemsLogsData, exportLineItemsLogsCSV, fetchLineItemsChartMetrics, fetchLineItemsTopStats } from './lineItemsLogSlice';

const enterpriseControlInputStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': { borderColor: '#CBD5E1' },
        '&.Mui-focused': {
            borderColor: '#1976d2',
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiSelect-select': {
        padding: '8px 14px !important',
        fontSize: '13px',
        color: '#1E293B',
        fontWeight: 500
    }
};

const utilityIconStyles = (isActive) => ({
    color: '#1976d2',
    padding: '8px',
    borderRadius: '10px',
    backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
    border: isActive ? '1px solid rgba(25, 118, 210, 0.2)' : '1px solid #E2E8F0',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        backgroundColor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(241, 245, 249, 0.8)',
        borderColor: isActive ? 'rgba(25, 118, 210, 0.3)' : '#CBD5E1',
        transform: 'translateY(-1px)'
    },
    '&:active': { transform: 'translateY(0)' }
});

const LineItemsLogReport = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    
    const { data: logsData, meta: serverMeta, loading, chartData, chartTopSkus, chartLoading, topSkus, topSkusLoading } = useSelector(state => state.lineItemsLog);
    const profile = useSelector(state => state.auth);
    const currentUserId = profile?.profile?.id || 3; 
    const lineColors = ['#1976d2', '#7C3AED', '#10B981', '#F59E0B', '#EF4444'];
    const [showFilter, setShowFilter] = useState(false);
    const [trackingDrawerVisible, setTrackingDrawerVisible] = useState(false);
    const [orderDrawerVisible, setOrderDrawerVisible] = useState(false);
    const [isMultiPiece, setIsMultiPiece] = useState(false);
    const [pageNumber, setPageNumber] = useState("")
    // 1. HELPER FUNCTION: Get standardized YYYY-MM-DD strings relative to today
    const getPastDateString = (daysAgo) => {
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);
        return date.toISOString().split('T')[0]; // Returns "YYYY-MM-DD" safely
    };

    // 2. INITIALIZE STATE LOCKING UNTO THE LAST ONE WEEK (7 DAYS)
    const [chartStartDate, setChartStartDate] = useState(() => getPastDateString(7));
    const [chartEndDate, setChartEndDate] = useState(() => getPastDateString(0)); //
   // 3. LIFECYCLE WATCHER TRACER TO SYNC WITH DISPATCH STACK
    useEffect(() => {
        dispatch(fetchLineItemsChartMetrics({ 
            start_date: chartStartDate, 
            end_date: chartEndDate 
        }));
    }, [chartStartDate, chartEndDate, dispatch]);
    const getInitialMetaDefaults = () => ({
        page: 1,
        per_page: 10,
        start_date: new Date(new Date().setDate(new Date().getDate() - 40)).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        event: "ALL",
        source: "ALL",
        search: "",
        sorting: JSON.stringify([{ field: 'created_at', sort: 'desc' }])
    });

    const [meta, setMeta] = useState(getInitialMetaDefaults());
    const skusList = useSelector(state => state.lineItemsLog?.skusList || []);
    // ─── 1. TRANSACTIONS TABLE LIFECYCLE (FIRES ON FIRST MOUNT + EVERY FILTER/PAGE CHANGE) ───
    useEffect(() => {
        dispatch(fetchLineItemsLogsData(meta));
    }, [dispatch, meta.page, meta.per_page, meta.event, meta.source, meta.sorting]);

    // ─── 2. TOP STATS VELOCITY RATING LIFECYCLE (FIRES *ONLY* ONCE ON PAGE LOAD / RELOAD) ───
    useEffect(() => {
        // We pass an empty object or your initial default meta state so it doesn't watch live mutations
        dispatch(fetchLineItemsTopStats({})); 
    }, [dispatch]); // 🚀 EMPTY DEPENDENCY ARRAY EXCLUDES LIVE FILTER WATCHERS



    // ─── ACTION TRIGGER MANAGEMENT PIPELINES ───
    const orderCallback = (orderId) => {
        if (!orderId) return;
        dispatch(fetchOrderDetail(orderId));
        setOrderDrawerVisible(true);
    };

    const trackingCallback = (carrierName, trackingNumber) => {
        if (!trackingNumber) return;
        let apikey = profile?.profile?.api_token || "";
        const data = { 'tracking_number': trackingNumber };
        if (trackingNumber.charAt(0) === 'M') {
            setIsMultiPiece(true);
        } else {
            setIsMultiPiece(false);
        }
        dispatch(fetchCustomerDetails(data, apikey));
        setTrackingDrawerVisible(true);
    };

    const onSortChange = (newSortModel) => {
        setMeta(prev => ({ ...prev, sorting: JSON.stringify(newSortModel) }));
    };

    const onChangePerPage = (event) => {
        setPageNumber('')
        setMeta(prev => ({ ...prev, page: 1, per_page: event.target.value }));
    };

    const onChangePagination = (event, page) => {
        setPageNumber('')
        setMeta(prev => ({ ...prev, page: page }));
    };

    const onInputChangePagination = (page) => {
        setPageNumber(page);
        const targetPage = page === '' ? 1 : page;
        setMeta(prev => ({ ...prev, page: targetPage }));
    };

    const handleFilterToggle = () => {
        setShowFilter(!showFilter);
    };

    const handleResetFilters = () => {
        const cleanResetState = getInitialMetaDefaults();
        setMeta(cleanResetState);
        dispatch(fetchLineItemsLogsData(cleanResetState));
    };

    const handleCSVExport = () => {
        dispatch(exportLineItemsLogsCSV(currentUserId, meta));
    };

    const extractStockValue = (jsonString, eventType = "") => {
        if (!jsonString) return "—";
        try {
            const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
            const normalizedEvent = String(eventType).toLowerCase();
            
            if (normalizedEvent.includes('kit_quantity_adjust')) {
                return parsed?.kitDetails?.quantity ?? parsed?.quantity ?? "0";
            }
            return parsed?.productLineItem?.quantity ?? parsed?.quantity ?? "0";
        } catch (e) {
            return "—";
        }
    };


    const columns = [
        {
            field: 'created_at',
            headerName: 'Date',
            headerClassName: 'super-app-theme--header',
            minWidth: 120,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                if (!params.value) return "—";
                
                try {
                    // ─── STAGE A: FORCE RAW STRING TYPE CONVERSION SO SPLIT NEVER FAILS ───
                    const rawString = String(params.value).trim();
                    
                    // Extract the date fragment (handles both "YYYY-MM-DD HH:MM:SS" and standard ISO "YYYY-MM-DDTHH:MM:SS...")
                    const datePart = rawString.includes('T') ? rawString.split('T')[0] : rawString.split(' ')[0];
                    const parts = datePart.split('-');
                    
                    if (parts.length !== 3) {
                        // Fallback: If it's a completely different format string, let the native parser try it out
                        const fallbackDate = new Date(rawString);
                        if (isNaN(fallbackDate.getTime())) return rawString;
                        return (
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                                {fallbackDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit', timeZone: 'UTC' }).toUpperCase()}
                            </Typography>
                        );
                    }
                    
                    // ─── STAGE B: CREATE A CLEAN MIDNIGHT UTC DATE INSTANCE ───
                    const year = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const day = parseInt(parts[2], 10);
                    
                    const dateObj = new Date(Date.UTC(year, month, day));
                    
                    if (isNaN(dateObj.getTime())) return String(params.value);
                    
                    return (
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                            {dateObj.toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: '2-digit',
                                timeZone: 'UTC' 
                            }).toUpperCase()}
                        </Typography>
                    );
                } catch (error) {
                    console.error("DataGrid Date Parsing Error Fallback:", error);
                    return String(params.value);
                }
            }
        },
        {
            field: 'order_id',
            headerName: 'Order ID',
            headerClassName: 'super-app-theme--header',
            minWidth: 140,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                if (!params.value) return "—";
                return (
                    <span 
                        style={{ color: 'rgb(24, 144, 255)', cursor: 'pointer', fontWeight: 600 }} 
                        onClick={() => orderCallback(params.value)}
                    >
                        {params.value}
                    </span>
                );
            }
        },
        {
            field: 'tracking_number',
            headerName: 'Tracking Number',
            headerClassName: 'super-app-theme--header',
            minWidth: 200,
            sortable: false,
            flex: 1.5,
            renderCell: (params) => {
                let tracking_number = params.value || "";
                let carrier_name = params.row.carrier_name || "";
                
                if (!tracking_number) {
                    const logPayloadString = params.row.inventory_items_log || params.row.log_after || params.row.log_before;
                    if (logPayloadString) {
                        try {
                            const parsedLog = typeof logPayloadString === 'string' 
                                ? JSON.parse(logPayloadString) 
                                : logPayloadString;
                            
                            tracking_number = parsedLog?.tracking_number || "";
                        } catch (e) {
                            tracking_number = "";
                        }
                    }
                }

                if (!tracking_number) return "—";

                let tracking = { "tracking_number": tracking_number, "carrier_name": carrier_name };
                return (
                    <CarrierTracking trackingItems={tracking} dispatchCallback={trackingCallback} carrierImageWidth="60px" />
                );
            }
        },
        {
            field: 'sku',
            headerName: 'SKU',
            headerClassName: 'super-app-theme--header',
            minWidth: 160,
            sortable: false,
            flex: 1.5,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontFamily: 'monospace', fontSize: '13px' }}>
                    {params.value || '—'}
                </Typography>
            )
        },
        {
            field: 'event',
            headerName: 'Type',
            headerClassName: 'super-app-theme--header',
            minWidth: 160,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                if (!params.value) return "—";
                
                const rawEvent = String(params.value).toLowerCase();
                let displayLabel = "";

                if (rawEvent === 'kit_quantity_adjust' || rawEvent === 'quantity_adjust') {
                    displayLabel = 'Order Out';
                } else if (rawEvent === 'manual_quantity_adjust' || rawEvent === 'void_kit_quantity_adjust' || rawEvent === 'void_quantity_adjust') {
                    displayLabel = 'Stock Update';
                } else {
                    displayLabel = rawEvent.includes('create') ? 'CREATE' : String(params.value).toUpperCase();
                }

                return (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', fontSize: '13px' }}>
                        {displayLabel}
                    </Typography>
                );
            }
        },
        {
            field: 'stock_before',
            headerName: 'Before Stock',
            headerClassName: 'super-app-theme--header',
            minWidth: 110,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => {
                // Directly renders the integer value from your database column field
                const value = params.value !== null && params.value !== undefined ? params.value : '—';
                return (
                    <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>
                        {value}
                    </Typography>
                );
            }
        },
        {
            field: 'stock_after',
            headerName: 'After Stock',
            headerClassName: 'super-app-theme--header',
            minWidth: 110,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => {
                const before = parseInt(params.row.stock_before) || 0;
                const after = parseInt(params.value) || 0;
                
                // ─── EVALUATE DIRECTIONAL SHIFT AGAINST STARTING BALANCE ───
                let textColor = '#334155'; // Default: Normal color for stable/unchanged values

                if (after < before) {
                    textColor = '#DC2626'; // 🔴 Case 1: Final stock is lower than starting stock (Loss)
                } else if (after > before) {
                    textColor = '#16A34A'; // 🟢 Case 2: Final stock is higher than starting stock (Gain)
                }
                
                const displayValue = params.value !== null && params.value !== undefined ? params.value : '—';
                
                return (
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: textColor, 
                            fontWeight: 700 
                        }}
                    >
                        {displayValue}
                    </Typography>
                );
            }
        },
        {
            field: 'quantity',
            headerName: 'Quantity',
            headerClassName: 'super-app-theme--header',
            minWidth: 110,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => {
                const diff = parseInt(params.value) || 0;
                
                let textColor = '#64748B'; 
                let prefix = '';

                if (diff > 0) {
                    textColor = '#16A34A'; 
                    prefix = '+';
                } else if (diff < 0) {
                    textColor = '#DC2626'; 
                }

                return (
                    <Typography variant="body2" sx={{ color: textColor, fontWeight: 700 }}>
                        {prefix}{diff}
                    </Typography>
                );
            }
        }
    ];

    function CustomTotalRows() {
        const rowsArray = Array.isArray(logsData) ? logsData : (logsData?.data || []);
        const currentCount = rowsArray.length || 0; 
        const totalCount = serverMeta?.total || logsData?.total || 0; 

        return (
            <Box className="custom-total-rows" sx={{ display: 'inline-flex', alignItems: 'center', py: 0.6, px: 1.5, borderRadius: '8px', backgroundColor: '#F1F5F9', border: '1px solid #E2E8F0' }}>
                <Typography variant="body2" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#475569' }}> 
                    Showing <strong style={{ color: '#0F172A', fontWeight: 700 }}>{currentCount}</strong> of <strong style={{ color: '#0F172A', fontWeight: 700 }}>{totalCount}</strong> 
                </Typography>
            </Box>
        );
    }

    function CustomPagesizeoptions() {
        return (
            <Box className="custom-page-size-options" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>Show</Typography>
                    <FormControl size="small" variant="outlined" sx={{ minWidth: 80, ...enterpriseControlInputStyles }}>
                        <Select value={meta?.per_page || 10} onChange={onChangePerPage}>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>entries</Typography>
                </Stack>
            </Box>
        );
    }

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, width: '100%', boxSizing: 'border-box' }}>
                <CustomPagesizeoptions />
                <CustomTotalRows />
            </GridToolbarContainer>
        );
    }

    function CustomPagination() {
        const currentPage = serverMeta?.current_page || 1;
        const lastPage = serverMeta?.last_page || 1;
        return (
            <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', boxSizing: 'border-box', '& .MuiPaginationItem-root': { fontSize: '13px', fontWeight: 600, borderRadius: '8px', '&.Mui-selected': { backgroundColor: '#1976d2', color: '#FFFFFF', '&:hover': { backgroundColor: '#115293' } } } }}>
                <Pagination color="primary" shape="circular" page={currentPage} count={lastPage} onChange={onChangePagination} />
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <PageSearch 
                        pagenumber={pageNumber} 
                        lastPage={serverMeta?.last_page || 1} 
                        getpagenumber={onInputChangePagination} 
                    />
                </Box>
            </Box>
        );
    }

    const gridRows = Array.isArray(logsData) ? logsData : (logsData?.data || []);

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box'}}>
            <Box className="sccardhead" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: '-0.5px', margin: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon sx={{ fontSize: '24px', color: '#1976d2' }} /> Inventory Activity Logs
                    </Typography>
                </Stack>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, p: 0, m: 0 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ArrowBackIcon sx={{ fontSize: '16px' }} />}
                        onClick={() => history.push('/inventory')} 
                        sx={{
                            textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '12.5px', height: '38px',
                            borderColor: '#E2E8F0', color: '#475569', backgroundColor: '#FFFFFF',
                            '&:hover': { backgroundColor: '#F8FAFC', borderColor: '#CBD5E1' }
                        }}
                    >
                        Back
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownloadIcon sx={{ fontSize: '16px' }} />}
                        onClick={handleCSVExport}
                        sx={{
                            textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '12.5px', height: '38px',
                            borderColor: '#E2E8F0', color: '#475569', px: 2, backgroundColor: '#FFFFFF',
                            '&:hover': { backgroundColor: '#F8FAFC', borderColor: '#CBD5E1' }
                        }}
                    >
                        Export CSV
                    </Button>
                    <Tooltip title="Filter Panels" placement="top">
                        <IconButton sx={utilityIconStyles(showFilter)} onClick={handleFilterToggle}>
                            <FilterAltOutlinedIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A', mb: 1.5, fontSize: '14px', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon sx={{ fontSize: '18px', color: '#475569' }} /> Top 5 SKUs by Stock Volume
                </Typography>
                
                {/* ─── SWITCHED TO STABLE STANDALONE ASYNC LOADING OVERLAY TRACER ─── */}
                {topSkusLoading ? (
                    <Box sx={{ width: '100%', py: 2 }}><LinearProgress /></Box>
                ) : (
                    <Grid container spacing={2}>
                        {(!topSkus || topSkus.length === 0) ? (
                            <Grid item xs={12}>
                                <Card sx={{ p: 2, textAlign: 'center', color: '#64748B', border: '1px solid #E2E8F0', borderRadius: '12px', boxShadow: 'none' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>No Order Out adjustments recorded in this window.</Typography>
                                </Card>
                            </Grid>
                        ) : (
                            topSkus.map((item, index) => {
                                // ─── FIXED: EXTRACT THE NEW REAL-TIME CURRENT STOCK BALANCE VALUE FROM BACKEND METRICS ───
                                const currentInventory = parseInt(item.currentStock) || 0;
                                const lineColors = ['#1976d2', '#7C3AED', '#10B981', '#F59E0B', '#EF4444'];

                                return (
                                    <Grid item xs={12} sm={6} md={2.4} key={item.sku}>
                                        <Card sx={{ p: 2.5, backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)', position: 'relative', overflow: 'hidden' }}>
                                            {/* Top-accent band cycling dynamically through our line color configurations */}
                                            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', backgroundColor: lineColors[index % lineColors.length] }} />
                                            
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                                {/* ─── CLEANED: REMOVED DUPLICATE MB PROP INTERACTION THAT TRIP-EXTENDED PADDING ─── */}
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        fontWeight: 700, 
                                                        color: '#0F172A', 
                                                        fontFamily: 'monospace', 
                                                        fontSize: '15px', 
                                                        whiteSpace: 'nowrap', 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis' 
                                                    }} 
                                                    title={item.sku}
                                                >
                                                    {item.sku}
                                                </Typography>
                                                
                                                <Box sx={{ 
                                                    px: 1, 
                                                    py: 0.25, 
                                                    borderRadius: '6px', 
                                                    backgroundColor: index === 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(100, 116, 139, 0.08)',
                                                    color: index === 0 ? '#1976d2' : '#475569'
                                                }}>
                                                    <Typography variant="caption" sx={{ fontSize: '10.5px', fontWeight: 700 }}>
                                                        Top {index + 1}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                            
                                            <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1, letterSpacing: '-0.5px' }}>
                                                    {currentInventory.toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '12px' }}>
                                                    in stock
                                                </Typography>
                                            </Stack>
                                        </Card>
                                    </Grid>
                                );
                            })
                        )}
                    </Grid>
                )}
            </Box>
            <Card sx={{ width: '100%', mb: 3, p: 2.5, backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    justifyContent="space-between" 
                    alignItems={{ xs: 'flex-start', sm: 'center' }} 
                    spacing={2} 
                    sx={{ mb: 2.5 }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '14px' }}>
                        Top 5 SKUs Adjustment Quantity Velocity Trend Line
                    </Typography>

                    {/* ─── CHART COMPACT DATE FILTER WRAPPER BOX ─── */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            type="date"
                            size="small"
                            label="Start Date"
                            value={chartStartDate}
                            onChange={(e) => setChartStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ 
                                width: '140px',
                                '& .MuiInputBase-input': { py: 0.75, fontSize: '12px' },
                                '& .MuiInputLabel-root': { fontSize: '12px' }
                            }}
                        />
                        <Typography sx={{ fontSize: '12px', color: '#64748B', fontWeight: 600 }}>to</Typography>
                        <TextField
                            type="date"
                            size="small"
                            label="End Date"
                            value={chartEndDate}
                            onChange={(e) => setChartEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ 
                                width: '140px',
                                '& .MuiInputBase-input': { py: 0.75, fontSize: '12px' },
                                '& .MuiInputLabel-root': { fontSize: '12px' }
                            }}
                        />
                    </Stack>
                </Stack>

                {chartLoading ? (
                    <Box sx={{ width: '100%', py: 6 }}><LinearProgress /></Box>
                ) : (
                    <Box sx={{ width: '100%', height: 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData || []} margin={{ top: 10, right: 15, left: -20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                <XAxis 
                                    dataKey="formatted_date" 
                                    stroke="#64748B" 
                                    fontSize={11} 
                                    fontWeight={600} 
                                    tickLine={false} 
                                    axisLine={false} 
                                />
                                <YAxis 
                                    stroke="#64748B" 
                                    fontSize={11} 
                                    fontWeight={600} 
                                    tickLine={false} 
                                    axisLine={false}
                                    allowDecimals={false}
                                    domain={[0, 'auto']} 
                                />
                                <ChartTooltip 
                                    contentStyle={{ 
                                        backgroundColor: '#FFFFFF', 
                                        borderRadius: '10px', 
                                        border: '1px solid #E2E8F0', 
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)' 
                                    }} 
                                    labelStyle={{ fontWeight: 700, color: '#0F172A', fontSize: '12px' }}
                                    formatter={(value, name) => [`${value.toLocaleString()} units`, name]}
                                />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', fontWeight: 600, pt: 10 }} />
                                
                                {(chartTopSkus || []).map((skuName, index) => {
                                    const baseColor = lineColors[index % lineColors.length];
                                    return (
                                        <React.Fragment key={skuName}>
                                            <Line 
                                                type="monotone" 
                                                dataKey={`${skuName}_order_out`} 
                                                name={`${skuName} (Order Out)`} 
                                                stroke={baseColor} 
                                                strokeWidth={3}
                                                activeDot={{ r: 5 }}
                                                dot={{ r: 2.5 }}
                                                connectNulls
                                            />
                                            <Line 
                                                type="monotone" 
                                                dataKey={`${skuName}_stock_update`} 
                                                name={`${skuName} (Stock Update)`} 
                                                stroke={baseColor} 
                                                strokeWidth={2}
                                                strokeDasharray="5 5"
                                                activeDot={{ r: 4 }}
                                                dot={{ r: 2 }}
                                                connectNulls
                                            />
                                        </React.Fragment>
                                    );
                                })}
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                )}
            </Card>

            <Collapse in={showFilter}>
                <Box sx={{ mb: 3 }}>
                    {/* ─── CARD WRAPPER TO BRING CONSISTENCY TO THE COLLAPSE AREA ─── */}
                    <Card 
                        sx={{ 
                            p: 2.5, 
                            backgroundColor: '#FFFFFF', 
                            border: '1px solid #E2E8F0', 
                            borderRadius: '16px', 
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)' 
                        }}
                    >
                        <LineItemsLogFilters 
                            meta={meta} 
                            onFilterChange={(updated) => setMeta(prev => ({ ...prev, ...updated }))} 
                            onSearchSubmit={() => {
                                setMeta(prev => ({ ...prev, page: 1 }));
                                dispatch(fetchLineItemsLogsData({ ...meta, page: 1 }));
                            }} 
                            onReset={handleResetFilters} 
                        />
                    </Card>
                </Box>
            </Collapse>
            <Card sx={{ width: '100%', backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)', overflow: 'hidden' }}>
                <Box sx={{ width: '100%', backgroundColor: '#FFFFFF' }}>
                    <DataGrid
                        sx={{
                            border: 'none',
                            backgroundColor: '#FFFFFF',
                            overflow: 'hidden',
                            '& .MuiDataGrid-topContainer': { backgroundColor: '#F8FAFC', '&::after': { display: 'none' } },
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', minHeight: '52px !important', maxHeight: '52px !important' },
                            '& .MuiDataGrid-columnHeader': { color: '#475569', fontWeight: 700, fontSize: '13px', '&:focus, &:focus-within': { outline: 'none' } },
                            '& .MuiDataGrid-row': { transition: 'background-color 0.15s ease-in-out', borderBottom: '1px solid #F1F5F9', alignItems: 'center', '&:hover': { backgroundColor: '#F8FAFC' }, '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.04)', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } } },
                            '& .MuiDataGrid-cell': { fontSize: '13.5px', color: '#334155', borderBottom: 'none', display: 'flex', alignItems: 'center', py: '12px', '&:focus, &:focus-within': { outline: 'none' } },
                            '& .MuiLinearProgress-root': { height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }
                        }}
                        getRowId={(row) => row.id}
                        rows={gridRows}
                        columns={columns}
                        loading={loading}
                        autoHeight={true}
                        paginationMode="server"
                        rowCount={serverMeta?.total || 0}
                        disableColumnFilter={true}
                        disableColumnMenu={true}
                        onSortModelChange={(newSortModel) => onSortChange(newSortModel)}
                        sortModel={meta.sorting ? JSON.parse(meta.sorting) : []}
                        disableRowSelectionOnClick
                        slots={{
                            loadingOverlay: LinearProgress,
                            footer: CustomPagination,
                            toolbar: CustomToolbar,
                            noRowsOverlay: NorowsData
                        }}
                    />
                </Box>
            </Card>

            <TrackingDrawer visible={trackingDrawerVisible} isMultiPiece={isMultiPiece} onClose={() => setTrackingDrawerVisible(false)} />
            <OrderDrawer visible={orderDrawerVisible} onClose={() => setOrderDrawerVisible(false)} />
        </Box>
    );
};

export default LineItemsLogReport;